<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoadingAuth || isLoading" class="flex items-center justify-center h-screen bg-gray-950">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
    </div>

    <!-- Not Logged In -->
    <div v-else-if="!user" class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center p-4">
      <div class="bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex items-center gap-3 border border-gray-700 shadow-lg">
        <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
        <span class="text-sm sm:text-base font-semibold text-gray-300">กรุณาเข้าสู่ระบบ</span>
      </div>
    </div>

    <!-- Main Modal -->
    <div v-else class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
      <div class="relative w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">
        
        <!-- Error -->
        <div v-if="error" class="p-2 bg-red-900 text-red-300 text-center text-sm rounded-md border border-red-800">
          {{ error }}
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-gray-700">
          <button
            @click="switchTab('location')"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'location' ? 'bg-gray-700 text-gray-300 border-b-2 border-indigo-600' : 'text-gray-400 hover:bg-gray-700'"
          >
            สถานที่
          </button>
          <button
            @click="switchTab('supernatural')"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="[
              activeTab === 'supernatural' ? 'bg-gray-700 text-gray-300 border-b-2 border-indigo-600' : 'text-gray-400 hover:bg-gray-700',
              !hasOccultismSkill ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            :disabled="!hasOccultismSkill"
            :title="!hasOccultismSkill ? 'ต้องเรียนรู้ทักษะไสยศาสตร์เพื่อเข้าถึงโลกวิญญาณ' : ''"
          >
            โลกวิญญาณ
          </button>
          <button
            @click="switchTab('settings')"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'settings' ? 'bg-gray-700 text-gray-300 border-b-2 border-indigo-600' : 'text-gray-400 hover:bg-gray-700'"
          >
            ตั้งค่า
          </button>
        </div>

        <!-- Body -->
        <div class="max-h-80 sm:max-h-96 overflow-y-auto space-y-3 scrollbar-thin">
          <!-- Location or Supernatural Tab -->
          <div v-if="activeTab === 'location' || activeTab === 'supernatural'" class="space-y-3">
            <!-- Vehicle (only for location tab) -->
            <div v-if="activeTab === 'location'" class="grid grid-cols-2 gap-3">
              <button
                @click="selectedVehicle = null"
                :class="[
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition-all duration-200',
                  selectedVehicle === null
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-700'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600'
                ]">
                <i class="fa-solid fa-person-walking text-xs"></i>
                <span class="text-xs sm:text-sm">เดินเท้า</span>
              </button>

              <button
                v-if="equippedVehicle"
                @click="selectedVehicle = equippedVehicle"
                :class="[
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition-all duration-200',
                  selectedVehicle && selectedVehicle.id === equippedVehicle.id
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-700'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600'
                ]"
                :aria-label="`เลือกพาหนะ ${equippedVehicle.name}`">
                <img
                  :src="equippedVehicle.img || 'https://via.placeholder.com/24'"
                  :alt="equippedVehicle.name"
                  class="w-4 h-4 object-cover rounded"
                  @error="equippedVehicle.img = 'https://via.placeholder.com/24'"
                />
                <span class="text-xs sm:text-sm">{{ equippedVehicle.name }} ({{ equippedVehicle.fuelLevel || 0 }})</span>
              </button>

              <div v-else class="col-span-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm bg-gray-700 text-gray-300 border border-gray-600 opacity-70">
                <i class="fas fa-motorcycle text-xs"></i>
                <span>ไม่มีพาหนะติดตั้ง</span>
              </div>
            </div>

            <!-- Map List -->
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-3 p-1">
              <div v-if="isLoading" class="col-span-full text-center text-gray-300 py-4 text-sm">กำลังโหลดแผนที่...</div>
              <div v-else-if="displayedMaps.length === 0" class="col-span-full text-center text-gray-300 py-4 text-sm">
                ไม่พบแผนที่
              </div>

              <div
                v-for="map in displayedMaps"
                :key="map.id"
                @click="selectMap(map)"
                class="cursor-pointer rounded-md border relative transition-all duration-200 group"
                :class="[
                  canAccessMap(map)
                    ? 'bg-gray-800 border-gray-700 hover:border-indigo-600 hover:shadow-md'
                    : 'bg-red-900 bg-opacity-70 border-red-800 hover:border-red-700',
                  (selectedMap && map.id === selectedMap.id) ? 'ring-2 ring-indigo-600' : ''
                ]"
                role="button"
                :aria-label="`เลือกแผนที่ ${map.name}`">
                
                <div class="relative w-full h-36">
                  <img
                    :src="map.imageUrl"
                    :alt="map.name"
                    class="w-full h-36 rounded-md object-cover"
                    @error="map.imageUrl = 'https://via.placeholder.com/150'"
                  />
                  <!-- Badge: ล่าสุด -->
                  <span
                    v-if="(activeTab === 'location' && map.id === lastVisitedLocationMapId) || (activeTab === 'supernatural' && map.id === lastVisitedSupernaturalMapId)"
                    class="absolute top-2 left-2 text-[10px] sm:text-xxs bg-indigo-600 text-white px-2 py-0.5 rounded border border-indigo-700 shadow">
                    ล่าสุด
                  </span>

                  <!-- ✅ Boss circles — มุมซ้ายบน เรียงแนวนอน เฉพาะแผนที่ที่ถูกเลือก -->
                  <div
                    v-if="activeTab === 'supernatural' && map.bossGhosts && map.bossGhosts.length > 0 && selectedMap && selectedMap.id === map.id"
                    class="absolute top-1.5 left-1.5 flex flex-row gap-1.5 items-start"
                  >
                    <div
                      v-for="boss in map.bossGhosts"
                      :key="getBossName(boss)"
                      class="relative group/boss"
                      style="width:40px;height:40px;"
                    >
                      <!-- SVG circular progress ring -->
                      <div class="relative" style="width:40px;height:40px;">
                        <svg
                          width="40" height="40"
                          viewBox="0 0 40 40"
                          class="absolute inset-0"
                          style="transform:rotate(-90deg);"
                        >
                          <!-- bg dark -->
                          <circle cx="20" cy="20" r="17" fill="#111827" fill-opacity="0.75"/>
                          <!-- Track -->
                          <circle cx="20" cy="20" r="15" fill="none" stroke="#374151" stroke-width="3"/>
                          <!-- Progress arc -->
                          <circle
                            cx="20" cy="20" r="15" fill="none"
                            :stroke="(bossStatuses[map.name]?.[getBossName(boss)]?.isAlive ?? true) ? '#22c55e' : '#f97316'"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            :stroke-dasharray="94.25"
                            :stroke-dashoffset="94.25 * (1 - (bossStatuses[map.name]?.[getBossName(boss)]?.progress ?? 1))"
                            style="transition: stroke-dashoffset 1s linear, stroke 0.3s ease;"
                          />
                        </svg>
                        <!-- รูปบอสวงกลมตรงกลาง -->
                        <div class="absolute inset-0 flex items-center justify-center" style="padding:5px;">
                          <img
                            v-if="bossStatuses[map.name]?.[getBossName(boss)]?.imageUrl"
                            :src="bossStatuses[map.name][getBossName(boss)].imageUrl"
                            :alt="getBossName(boss)"
                            class="w-full h-full rounded-full object-cover"
                            @error="$event.target.style.display='none'"
                          />
                          <i v-else class="fas fa-skull-crossbones text-gray-300" style="font-size:11px;"></i>
                        </div>
                      </div>

                      <!-- Countdown text ใต้วงกลม — ซ่อน -->

                      <!-- Tooltip hover -->
                      <div class="absolute top-11 left-0 z-20 hidden group-hover/boss:flex flex-col gap-0.5 bg-gray-900 text-white text-[10px] px-2 py-1.5 rounded whitespace-nowrap border border-gray-600 shadow-lg pointer-events-none">
                        <span class="font-semibold text-gray-200">{{ getBossName(boss) }}</span>
                        <span :class="(bossStatuses[map.name]?.[getBossName(boss)]?.isAlive ?? true) ? 'text-green-400' : 'text-orange-400'">
                          {{ (bossStatuses[map.name]?.[getBossName(boss)]?.isAlive ?? true)
                            ? '✅ เกิดแล้ว'
                            : `⏳ ${bossStatuses[map.name]?.[getBossName(boss)]?.respawnTime || '...'}` }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- overlay -->
                  <div class="absolute bottom-0 left-0 right-0 p-2 bg-gray-800 bg-opacity-60 rounded-b-md">
                    <h3 class="text-sm font-semibold text-gray-300">{{ map.name }}</h3>
                    <div class="mt-1 text-xxs sm:text-xs text-gray-300 space-y-0.5">
                      <p>เลเวลขั้นต่ำ: <span class="font-semibold text-gray-100">{{ map.requiredLevel }}</span></p>
                      <p v-if="map.allowedBuildings && map.allowedBuildings.length > 0" class="text-yellow-400">ตึก: <span class="font-semibold">{{ map.allowedBuildings.join(', ') }}</span></p>
                      <p v-if="map.type === 'สถานที่'">จำนวนซอมบี้: <span class="font-semibold text-gray-100">{{ map.minZombies || 0 }} - {{ map.maxZombies || 0 }}</span></p>
                      <p v-else-if="map.type === 'โลกวิญญาณ'">จำนวนวิญญาณ: <span class="font-semibold text-gray-100">{{ map.minGhosts || 0 }} - {{ map.maxGhosts || 0 }}</span></p>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2 text-xxs">
                      <span v-if="map.requiredCoins" class="inline-flex items-center gap-1 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                        <i class="fa-solid fa-coins text-white text-xxs"></i>{{ map.requiredCoins }}
                      </span>
                      <span v-if="map.requiredGold" class="inline-flex items-center gap-1 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                        <i class="fa-solid fa-g text-yellow-500 text-xxs"></i>{{ map.requiredGold }}
                      </span>
                      <span v-if="map.requiredDiamonds" class="inline-flex items-center gap-1 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                        <i class="fa-solid fa-gem text-blue-300 text-xxs"></i>{{ map.requiredDiamonds }}
                      </span>
                      <span v-if="map.requiredCash" class="inline-flex items-center gap-1 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                        <i class="fa-solid fa-money-bill text-green-300 text-xxs"></i>{{ map.requiredCash }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Load More -->
            <div v-if="hasMoreMaps" class="flex justify-center">
              <button
                @click="loadMoreMaps"
                class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md border border-indigo-700 transition-all duration-200">
                โหลดเพิ่ม
              </button>
            </div>
          </div>

          <!-- Settings -->
          <div v-else-if="activeTab === 'settings'">
            <div v-if="isLoading" class="text-center text-gray-300 py-4 text-sm">กำลังโหลดแผนที่...</div>
            <div v-else-if="maps.length === 0" class="text-center text-gray-300 py-4 text-sm">ไม่พบแผนที่</div>

            <div v-else class="space-y-2">
              <!-- สวิตช์สำหรับปิด/เปิดการลงแผนที่ล่าสุด -->
              <div class="flex items-center justify-between p-3 bg-gray-800 rounded-md border border-gray-700">
                <span class="text-sm text-gray-300 font-semibold">แสดงแผนที่ล่าสุดเป็นอันดับแรก</span>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="showLastVisitedFirst"
                    @change="saveShowLastVisitedFirst"
                    class="sr-only"
                    aria-label="แสดงหรือปิดการแสดงแผนที่ล่าสุดเป็นอันดับแรก"
                  />
                  <div
                    :class="[
                      'w-10 h-5 rounded-full transition-colors duration-200 border',
                      showLastVisitedFirst ? 'bg-indigo-600 border-indigo-700' : 'bg-red-800 border-red-900'
                    ]">
                    <div
                      :class="[
                        'w-3 h-3 bg-white rounded-full transform transition-transform duration-200 mt-1',
                        showLastVisitedFirst ? 'translate-x-6' : 'translate-x-1'
                      ]"></div>
                  </div>
                </label>
              </div>

              <!-- รายการพลังงาน -->
              <div
                v-for="map in maps"
                :key="map.id"
                class="flex items-center justify-between p-3 bg-gray-800 rounded-md border border-gray-700">
                <span class="text-sm text-gray-300 font-semibold">{{ map.name }}</span>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="!hiddenMaps.includes(map.id)"
                    @change="toggleMapVisibility(map.id)"
                    class="sr-only"
                    :aria-label="`แสดงหรือซ่อนแผนที่ ${map.name}`"
                  />
                  <div
                    :class="[
                      'w-10 h-5 rounded-full transition-colors duration-200 border',
                      hiddenMaps.includes(map.id) ? 'bg-red-800 border-red-900' : 'bg-indigo-600 border-indigo-700'
                    ]">
                    <div
                      :class="[
                        'w-3 h-3 bg-white rounded-full transform transition-transform duration-200 mt-1',
                        hiddenMaps.includes(map.id) ? 'translate-x-1' : 'translate-x-6'
                      ]"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Map Info -->
        <transition name="fade" v-if="activeTab === 'location' || activeTab === 'supernatural'">
          <div
            v-if="selectedMap && canAccessMap(selectedMap)"
            class="p-3 bg-gray-800 rounded-md border border-gray-700">
            <div class="text-xs sm:text-sm text-center font-semibold text-gray-300 mb-2">
              ทรัพยากรที่ต้องใช้เมื่อเข้าแผนที่ {{ selectedMap.name }}
              <span v-if="selectedVehicle">ด้วย {{ selectedVehicle.name }}</span>
            </div>
            <div class="flex justify-center items-center flex-wrap gap-2 text-xxs sm:text-xs">
              <div v-if="activeTab === 'location' && selectedVehicle && selectedVehicle.fuelLevel >= 1" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fas fa-gas-pump text-xs"></i> น้ำมัน 1
              </div>
              <div v-if="selectedMap.requiredEnergy && (!selectedVehicle || activeTab === 'supernatural')" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fas fa-bolt text-xs"></i> พลังงาน {{ selectedMap.requiredEnergy }}
              </div>
              <div v-if="selectedMap.reduceHappy" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fas fa-frown text-xs"></i> ลดความสุข {{ selectedMap.reduceHappy }}
              </div>
              <div v-if="selectedMap.requiredCoins" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fa-solid fa-coins text-xs"></i> เหรียญเงิน {{ selectedMap.requiredCoins }}
              </div>
              <div v-if="selectedMap.requiredGold" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fa-solid fa-g text-xs"></i> ทอง {{ selectedMap.requiredGold }}
              </div>
              <div v-if="selectedMap.requiredDiamonds" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fa-solid fa-gem text-xs"></i> เพชร {{ selectedMap.requiredDiamonds }}
              </div>
              <div v-if="selectedMap.requiredCash" class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600">
                <i class="fa-solid fa-money-bill text-xs"></i> แคช {{ selectedMap.requiredCash }}
              </div>
              <div
                v-for="itemName in selectedMap.requiredItems"
                :key="itemName"
                class="flex items-center gap-1 bg-gray-700 px-2 py-1 rounded border border-gray-600"
              >
                <img
                  v-if="itemDetails[itemName]?.img"
                  :src="itemDetails[itemName].img"
                  :alt="itemName"
                  class="w-4 h-4 object-cover rounded"
                  @error="itemDetails[itemName].img = 'https://via.placeholder.com/24'"
                />
                <span>{{ itemName }} 1</span>
              </div>
            </div>
          </div>

          <div v-else-if="selectedMap" class="p-3 bg-red-900 rounded-md border border-red-800">
            <div class="text-xs sm:text-sm text-center font-semibold text-red-300 mb-2">
              คุณต้องมีทรัพยากรต่อไปนี้เพื่อเข้าถึงแผนที่ {{ selectedMap.name }}
              <span v-if="activeTab === 'location' && selectedVehicle">ด้วย {{ selectedVehicle.name }}</span>
            </div>
            <div class="flex justify-center items-center flex-wrap gap-2 text-xxs sm:text-xs">
              <div v-if="activeTab === 'location' && selectedVehicle && selectedVehicle.fuelLevel < 1" class="flex items-center gap-1 bg-gray-800 px-2 py-1 rounded border border-red-800">
                <i class="fas fa-gas-pump text-xs"></i> น้ำมัน 1
              </div>
              <div
                v-for="item in insufficientResources"
                :key="item"
                class="flex items-center gap-1 bg-gray-800 px-2 py-1 rounded border border-red-800">
                <i :class="getResourceIcon(item)" class="text-xs"></i>
                <span>{{ item }}</span>
              </div>
            </div>

            <div
              v-if="insufficientResources.some(item => item.includes('จำนวนห้อง'))"
              class="mt-3 text-xs sm:text-sm text-center text-gray-300">
              คุณต้องไปซื้อห้องที่ผู้จัดการเพื่อเข้าแผนที่นี้
              <router-link :to="{ name: 'ManagerRoom' }" class="text-indigo-400 hover:text-indigo-300 underline">
                ไปที่ผู้จัดการ
              </router-link>
            </div>
          </div>
        </transition>


        <!-- Footer Actions -->
        <div v-if="activeTab === 'location' || activeTab === 'supernatural'" class="flex justify-end gap-3 border-t border-gray-700 pt-3">
          <button
            v-if="selectedMap"
            @click="showMapDetails = true"
            class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md border border-indigo-700 transition-all duration-200 flex items-center gap-2">
            <i class="fas fa-info-circle text-xs"></i>
            <span class="hidden sm:block">ดูรายละเอียด</span>
          </button>

          <button
            v-if="selectedMap"
            @click="exploreMap"
            :disabled="!canAccessMap(selectedMap) || (activeTab === 'location' && selectedVehicle && selectedVehicle.fuelLevel < 1) || isExploring || hasActiveEvent"
            :class="[
              'text-white text-xs sm:text-sm py-2 px-4 rounded-md border transition-all duration-200 flex items-center gap-2',
              (!canAccessMap(selectedMap) || (activeTab === 'location' && selectedVehicle && selectedVehicle.fuelLevel < 1) || isExploring || hasActiveEvent)
                ? 'bg-indigo-600 border-indigo-700 opacity-60 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 border-indigo-700'
            ]">
            <span v-if="!isExploring && !hasActiveEvent">
              <i class="fas" :class="activeTab === 'location' && selectedVehicle ? 'fa-motorcycle' : 'fa-hiking'"></i>
              {{ activeTab === 'location' && selectedVehicle ? 'ใช้งานพาหนะและสำรวจ' : 'สำรวจ' }}
            </span>
            <span v-else-if="hasActiveEvent">
              <i class="fas fa-exclamation-triangle text-xs"></i>
              อยู่ในเหตุการณ์
            </span>
            <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>

          <button
            @click="cancelModal"
            class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group">
            <i class="fas fa-times mr-1 text-xs group-hover:scale-110"></i> ปิด
          </button>
        </div>

        <!-- Map Details Modal -->
        <MapDetailsModal
          v-if="showMapDetails && selectedMap && selectedMap.type === 'สถานที่'"
          :map="selectedMap"
          @close="showMapDetails = false"
          class="modal"
        />
        <SupernaturalMapDetailsModal
          v-if="showMapDetails && selectedMap && selectedMap.type === 'โลกวิญญาณ'"
          :map="selectedMap"
          @close="showMapDetails = false"
          class="modal"
        />
      </div>
    </div>
  </div>
</template>

<script>
import getUser from '@/composables/getUser';
import { projectFirestore } from '@/firebase/config';
import { saveLastExploreContext, exploreMapByName } from '@/composables/useQuickExplore';
import { ref, onUnmounted, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import MapDetailsModal from '@/components/MapDetailsModal.vue';
import SupernaturalMapDetailsModal from '@/components/SupernaturalMapDetailsModal.vue';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'ExploreMapModal',
  components: { MapDetailsModal, SupernaturalMapDetailsModal },
  emits: ['cancelModal'],
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();

    const isLoading = ref(true);
    const error = ref(null);
    const maps = ref([]);
    const selectedMap = ref(null);
    const selectedVehicle = ref(null);
    const storedVehicle = ref(null);
    const inventoryItems = ref([]);
    const itemDetails = ref({});
    const insufficientResources = ref([]);
    const activeTab = ref('location');
    const hiddenMaps = ref([]);
    const isExploring = ref(false);
    const mapPage = ref(0);
    const mapsPerPage = 99;
    const showBackToTop = ref(false);
    const showMapDetails = ref(false);
    const initialFetchDone = ref(false);
    const lastVisitedLocationMapId = ref(localStorage.getItem('lastVisitedLocationMapId') || null);
    const lastVisitedSupernaturalMapId = ref(localStorage.getItem('lastVisitedSupernaturalMapId') || null);
    const showLastVisitedFirst = ref(true);

    const bossStatuses = ref({});
    let bossCountdownInterval = null;
    const bossImageCache = {};

    const playerData = computed(() => {
      const ud = playerStore.userData || {};
      const cd = playerStore.characterData || {};
      const cpd = playerStore.characterPrivateData || {};

      return {
        gold: cd.gold || 0,
        cash: ud.cash || 0,
        level: cd.level || 0,
        silver: cd.silver || 0,
        renown: cd.renown || 0,
        diamond: cd.diamond || 0,
        building: cd.building || 1,
        energyLevel: cpd.energyLevel || 0,
        room: cd.room || 0,
        happyLevel: cpd.happyLevel || 0,
        unlockedMaps: cpd.unlockedMaps || [],
        learnedSkills: cpd.learnedSkills || []
      };
    });

    const equippedVehicle = computed(() => {
      const cpd = playerStore.characterPrivateData;
      if (!cpd?.vehicleData) return null;

      const template = gameItemsData[cpd.vehicleData.templateId] || {};

      return {
        id: cpd.vehicle,
        instanceId: cpd.vehicle,
        ...template,
        ...cpd.vehicleData,
        fuelLevel: cpd.vehicleData.states?.fuelLevel || 0
      };
    });

    const isAdmin = computed(() => playerStore.userData?.admin === true);

    watch(isAdmin, () => {
      if (initialFetchDone.value) {
        setupMapListener();
      }
    });

    const hasActiveEvent = computed(() => {
      return !!playerStore.characterPrivateData?.event;
    });

    const customMapOrder = {
      location: [
        'ตรอกซอย', 'ตลาด', 'สนามกอล์ฟ', 'โรงเรียน', 'มหาวิทยาลัย', 'โกดัง', 'รถไฟฟ้าใต้ดิน',
        'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
        'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'ราชมังคลากีฬาสถาน',
        'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'พระบรมมหาราชวัง', 'ป่าช้า', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ',
        'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม', 'เหมืองแร่ร้าง', 'ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์', 'สถานกักกันสิ่งมีชีวิตกลายพันธุ์'
      ],
      supernatural: [
        'ตลาดวิญญาณ', 'สนามกอล์ฟวิญญาณ', 'โรงเรียนวิญญาณ', 'มหาวิทยาลัยวิญญาณ', 'รถไฟฟ้าใต้ดินวิญญาณ',
        'โรงพยาบาลวิญญาณ', 'หมู่บ้านคนรวยวิญญาณ', 'วัดวิญญาณ', 'สวนสนุกวิญญาณ',
        'โรงงานอุตสาหกรรมวิญญาณ', 'ห้างสรรพสินค้าวิญญาณ', 'สถานีดับเพลิงวิญญาณ', 'ค่ายทหารวิญญาณ', 'พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'
      ]
    };

    const hasOccultismSkill = computed(() => {
      return playerData.value?.learnedSkills?.includes('หนังสือไสยศาสตร์') || false;
    });

    const loadActiveTab = () => {
      const stored = localStorage.getItem('activeTab');
      if (stored) {
        activeTab.value = stored;
        if (activeTab.value === 'supernatural' && !hasOccultismSkill.value) {
          activeTab.value = 'location';
        }
      }
    };

    const saveActiveTab = () => {
      localStorage.setItem('activeTab', activeTab.value);
    };

    const loadHiddenMaps = () => {
      const storedHiddenMaps = localStorage.getItem('hiddenMaps');
      hiddenMaps.value = storedHiddenMaps ? JSON.parse(storedHiddenMaps) : [];
    };

    const saveHiddenMaps = () => {
      localStorage.setItem('hiddenMaps', JSON.stringify(hiddenMaps.value));
    };

    const toggleMapVisibility = (mapId) => {
      if (hiddenMaps.value.includes(mapId)) {
        hiddenMaps.value = hiddenMaps.value.filter(id => id !== mapId);
      } else {
        hiddenMaps.value.push(mapId);
        if (selectedMap.value && selectedMap.value.id === mapId) {
          selectedMap.value = null;
          insufficientResources.value = [];
        }
      }

      saveHiddenMaps();
    };

    const loadShowLastVisitedFirst = () => {
      const stored = localStorage.getItem('showLastVisitedFirst');
      showLastVisitedFirst.value = stored !== null ? JSON.parse(stored) : true;
    };

    const saveShowLastVisitedFirst = () => {
      localStorage.setItem('showLastVisitedFirst', JSON.stringify(showLastVisitedFirst.value));
    };

    const unsubscribeBossStatuses = () => {
      if (bossCountdownInterval) {
        clearInterval(bossCountdownInterval);
        bossCountdownInterval = null;
      }

      bossStatuses.value = {};
    };

    const switchTab = (tab) => {
      if (tab === 'supernatural' && !hasOccultismSkill.value) {
        error.value = 'คุณต้องเรียนรู้ทักษะไสยศาสตร์เพื่อเข้าถึงโลกวิญญาณ';
        return;
      }

      if (tab === 'supernatural') {
        storedVehicle.value = selectedVehicle.value;
        selectedVehicle.value = null;
      } else if (tab === 'location') {
        selectedVehicle.value = storedVehicle.value;
      }

      activeTab.value = tab;
      saveActiveTab();
      selectedMap.value = null;
      insufficientResources.value = [];
      mapPage.value = 0;
      showMapDetails.value = false;
      unsubscribeBossStatuses();
    };

    const fetchItemDetails = (itemName) => {
      if (itemDetails.value[itemName]) return;

      const item = gameItemsData[itemName];

      itemDetails.value[itemName] = item || {
        img: 'https://via.placeholder.com/24'
      };
    };

    const processInventory = async () => {
      const bagData = inventoryStore.bagData;

      if (!bagData || !bagData.items) {
        inventoryItems.value = [];
        return;
      }

      const bagItems = bagData.items;

      if (Array.isArray(bagItems) || Object.keys(bagItems).length === 0) {
        inventoryItems.value = [];
        return;
      }

      inventoryItems.value = Object.entries(bagItems)
        .map(([instanceId, item]) => {
          if (!item?.templateId) return null;

          const template = gameItemsData[item.templateId] || {};
          const states = item.states || {};

          return {
            id: instanceId,
            instanceId,
            templateId: item.templateId,
            ...template,
            ...states,
            states,
            name: states.name || template.name || item.templateId
          };
        })
        .filter((item) => item);

      inventoryItems.value.forEach((item) => {
        if (!itemDetails.value[item.name]) {
          fetchItemDetails(item.name);
        }
      });
    };

    watch(() => inventoryStore.bagData, processInventory, { immediate: true });

    watch(() => playerStore.characterPrivateData?.event, (eventId) => {
      if (eventId) {
        error.value = 'ตัวละครกำลังอยู่ในเหตุการณ์ ไม่สามารถออกสำรวจได้';
      } else if (error.value === 'ตัวละครกำลังอยู่ในเหตุการณ์ ไม่สามารถออกสำรวจได้') {
        error.value = '';
      }
    }, { immediate: true });

    const setupMapListener = async () => {
      try {
        isLoading.value = true;

        const snapshot = await projectFirestore
          .collection('maps')
          .get();

        let fetchedMaps = snapshot.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || 'Unknown',
            type: doc.data().type || 'สถานที่',
            isActive: doc.data().isActive !== undefined ? doc.data().isActive : true,
            requiredSkills: doc.data().requiredSkills || [],
            requiredItems: doc.data().requiredItems || [],
            ...doc.data(),
            isRecommended: doc.data().isRecommended || false,
            reduceHappy: doc.data().reduceHappy || 0
          }));

        fetchedMaps = fetchedMaps.filter(map => isAdmin.value || map.isActive === true);

        const locationMaps = fetchedMaps
          .filter(map => map.type === 'สถานที่')
          .sort((a, b) => {
            const order = customMapOrder.location;
            const indexA = order.indexOf(a.name);
            const indexB = order.indexOf(b.name);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return a.name.localeCompare(b.name, 'th');
          });

        const supernaturalMaps = fetchedMaps
          .filter(map => map.type === 'โลกวิญญาณ')
          .sort((a, b) => {
            const order = customMapOrder.supernatural;
            const indexA = order.indexOf(a.name);
            const indexB = order.indexOf(b.name);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return a.name.localeCompare(b.name, 'th');
          });

        maps.value = [...locationMaps, ...supernaturalMaps];

        fetchedMaps.forEach(map => {
          if (map.type === 'โลกวิญญาณ' && map.requiredItems) {
            map.requiredItems.forEach(itemName => fetchItemDetails(itemName));
          }
        });

        isLoading.value = false;
      } catch (err) {
        error.value = 'ไม่สามารถดึงข้อมูลแผนที่ได้: ' + err.message;
        isLoading.value = false;
        console.error('Error fetching maps:', err);
      }
    };

    const displayedMaps = computed(() => {
      const baseList = maps.value
        .filter(map => !hiddenMaps.value.includes(map.id))
        .filter(map => activeTab.value === 'location' ? map.type === 'สถานที่' : map.type === 'โลกวิญญาณ');

      let arranged = baseList.slice();

      if (showLastVisitedFirst.value) {
        const lastVisitedMapId = activeTab.value === 'location'
          ? lastVisitedLocationMapId.value
          : lastVisitedSupernaturalMapId.value;

        if (lastVisitedMapId) {
          const idx = arranged.findIndex(m => m.id === lastVisitedMapId);
          if (idx > -1) {
            const [lastItem] = arranged.splice(idx, 1);
            arranged.unshift(lastItem);
          }
        }
      }

      const start = mapPage.value * mapsPerPage;
      return arranged.slice(0, start + mapsPerPage);
    });

    const hasMoreMaps = computed(() => {
      const mapList = maps.value
        .filter(map => !hiddenMaps.value.includes(map.id))
        .filter(map => activeTab.value === 'location' ? map.type === 'สถานที่' : map.type === 'โลกวิญญาณ');

      return mapList.length > (mapPage.value + 1) * mapsPerPage;
    });

    const canAccessMap = (map) => {
      if (!playerData.value) return false;

      if (map.requiredMap && !playerData.value.unlockedMaps.includes(map.requiredMap)) return false;

      if (map.allowedBuildings && Array.isArray(map.allowedBuildings) && map.allowedBuildings.length > 0) {
        const playerBuilding = Number(playerData.value.building) || 1;
        if (!map.allowedBuildings.map(Number).includes(playerBuilding)) {
          return false;
        }
      }

      if (map.type === 'โลกวิญญาณ' && map.requiredSkills && map.requiredSkills.length > 0) {
        const missingSkills = map.requiredSkills.filter(skill => !playerData.value.learnedSkills.includes(skill));
        if (missingSkills.length > 0) return false;
      }

      if (map.type === 'โลกวิญญาณ' && map.requiredItems && map.requiredItems.length > 0) {
        const itemCounts = {};

        inventoryItems.value.forEach(item => {
          itemCounts[item.name] = (itemCounts[item.name] || 0) + 1;
        });

        const missingItems = map.requiredItems.filter(itemName => (itemCounts[itemName] || 0) < 1);
        if (missingItems.length > 0) return false;
      }

      return (
        playerData.value.level >= (map.requiredLevel || 0) &&
        playerData.value.renown >= (map.requiredRenown || 0) &&
        playerData.value.diamond >= (map.requiredDiamonds || 0) &&
        playerData.value.silver >= (map.requiredCoins || 0) &&
        playerData.value.gold >= (map.requiredGold || 0) &&
        playerData.value.cash >= (map.requiredCash || 0) &&
        playerData.value.room >= (map.roomRequired || 0) &&
        playerData.value.happyLevel >= (map.reduceHappy || 0) &&
        (!selectedVehicle.value || selectedVehicle.value.fuelLevel >= 1) &&
        (!map.requiredEnergy || playerData.value.energyLevel >= map.requiredEnergy || (activeTab.value === 'location' && selectedVehicle.value))
      );
    };

    const formatBossCountdown = (diff) => {
      if (diff <= 0) return 'เกิดแล้ว';

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      if (hours > 0) return `${hours}ชม.${minutes}น.`;
      if (minutes > 0) return `${minutes}น.${seconds}ว.`;

      return `${seconds}ว.`;
    };

    const getBossName = (boss) => {
      return typeof boss === 'string' ? boss : boss?.name || String(boss);
    };

    const startBossCountdown = () => {
      if (bossCountdownInterval) {
        clearInterval(bossCountdownInterval);
        bossCountdownInterval = null;
      }

      bossCountdownInterval = setInterval(() => {
        const updated = JSON.parse(JSON.stringify(bossStatuses.value));
        let changed = false;

        Object.keys(updated).forEach(mapName => {
          Object.keys(updated[mapName]).forEach(bossName => {
            const s = updated[mapName][bossName];

            if (!s.isAlive && s.nextRespawn) {
              const diff = Math.max(0, s.nextRespawn - Date.now());
              const newAlive = diff <= 0;
              const newTime = newAlive ? '' : formatBossCountdown(diff);
              const newProgress = newAlive
                ? 1
                : (s.respawnDuration > 0 ? Math.max(0, Math.min(1, 1 - (diff / s.respawnDuration))) : 0);

              updated[mapName][bossName] = {
                ...s,
                isAlive: newAlive,
                respawnTime: newTime,
                progress: newProgress
              };

              changed = true;
            }
          });
        });

        if (changed) {
          bossStatuses.value = updated;
        }
      }, 1000);
    };

    const loadBossStatusesOnce = async (supernaturalMaps) => {
      unsubscribeBossStatuses();

      const mapsWithBoss = supernaturalMaps.filter(m => m.bossGhosts?.length > 0);
      if (!mapsWithBoss.length) return;

      for (const map of mapsWithBoss) {
        for (const boss of map.bossGhosts) {
          const bossName = getBossName(boss);

          if (!bossImageCache[bossName]) {
            const ghostTemplate = gameItemsData[bossName];

            if (ghostTemplate?.imageUrl || ghostTemplate?.img) {
              bossImageCache[bossName] = ghostTemplate.imageUrl || ghostTemplate.img || null;
            } else {
              try {
                const ghostDoc = await projectFirestore
                  .collection('gameGhosts')
                  .doc(bossName)
                  .get();

                bossImageCache[bossName] = ghostDoc.exists
                  ? (ghostDoc.data().imageUrl || ghostDoc.data().img || null)
                  : null;
              } catch (err) {
                bossImageCache[bossName] = null;
              }
            }
          }

          const docId = `${map.name}_${bossName}`;

          try {
            const doc = await projectFirestore
              .collection('bossGhostStatus')
              .doc(docId)
              .get();

            const data = doc.exists ? doc.data() : {};
            const isAlive = data.isAlive ?? true;
            const nextRespawn = data.nextRespawn ? data.nextRespawn.toMillis() : null;
            const updatedAt = data.updatedAt ? data.updatedAt.toMillis() : null;

            const respawnDuration = (nextRespawn && updatedAt && nextRespawn > updatedAt)
              ? nextRespawn - updatedAt
              : 10800000;

            const diff = nextRespawn ? Math.max(0, nextRespawn - Date.now()) : 0;
            const progress = isAlive
              ? 1
              : (respawnDuration > 0 ? Math.max(0, Math.min(1, 1 - (diff / respawnDuration))) : 0);

            bossStatuses.value = {
              ...bossStatuses.value,
              [map.name]: {
                ...(bossStatuses.value[map.name] || {}),
                [bossName]: {
                  isAlive,
                  nextRespawn,
                  respawnDuration,
                  progress,
                  imageUrl: bossImageCache[bossName] ?? null,
                  respawnTime: isAlive ? '' : formatBossCountdown(diff)
                }
              }
            };
          } catch (err) {
            console.error('Error loading boss status:', docId, err);

            bossStatuses.value = {
              ...bossStatuses.value,
              [map.name]: {
                ...(bossStatuses.value[map.name] || {}),
                [bossName]: {
                  isAlive: true,
                  nextRespawn: null,
                  respawnDuration: 10800000,
                  progress: 1,
                  imageUrl: bossImageCache[bossName] ?? null,
                  respawnTime: ''
                }
              }
            };
          }
        }
      }

      startBossCountdown();
    };

    const selectMap = async (map) => {
      selectedMap.value = map;

      if (activeTab.value === 'supernatural') {
        selectedVehicle.value = null;
      }

      if (map.type === 'โลกวิญญาณ' && map.bossGhosts?.length > 0) {
        await loadBossStatusesOnce([map]);
      } else {
        unsubscribeBossStatuses();
      }

      if (!canAccessMap(map)) {
        checkInsufficientResources(map);
      } else {
        insufficientResources.value = [];
      }
    };

    const checkInsufficientResources = (map) => {
      insufficientResources.value = [];

      if (map.requiredMap && !playerData.value.unlockedMaps.includes(map.requiredMap)) {
        insufficientResources.value.push(`แผนที่ ${map.requiredMap}`);
      }

      if (map.allowedBuildings && Array.isArray(map.allowedBuildings) && map.allowedBuildings.length > 0) {
        const playerBuilding = Number(playerData.value.building) || 1;
        if (!map.allowedBuildings.map(Number).includes(playerBuilding)) {
          insufficientResources.value.push(`ต้องอยู่ตึก ${map.allowedBuildings.join(' หรือ ')}`);
        }
      }

      if (playerData.value.level < (map.requiredLevel || 0)) {
        insufficientResources.value.push(`เลเวล ${map.requiredLevel}`);
      }

      if (playerData.value.room < (map.roomRequired || 0)) {
        insufficientResources.value.push(`จำนวนห้อง ${map.roomRequired}`);
      }

      if (playerData.value.renown < (map.requiredRenown || 0)) {
        insufficientResources.value.push(`ชื่อเสียง ${map.requiredRenown}`);
      }

      if (map.requiredEnergy && (!selectedVehicle.value || activeTab.value === 'supernatural') && playerData.value.energyLevel < (map.requiredEnergy || 0)) {
        insufficientResources.value.push(`พลังงาน ${map.requiredEnergy}`);
      }

      if (playerData.value.silver < (map.requiredCoins || 0)) {
        insufficientResources.value.push(`เหรียญเงิน ${map.requiredCoins}`);
      }

      if (playerData.value.gold < (map.requiredGold || 0)) {
        insufficientResources.value.push(`ทอง ${map.requiredGold}`);
      }

      if (playerData.value.diamond < (map.requiredDiamonds || 0)) {
        insufficientResources.value.push(`เพชร ${map.requiredDiamonds}`);
      }

      if (playerData.value.cash < (map.requiredCash || 0)) {
        insufficientResources.value.push(`แคช ${map.requiredCash}`);
      }

      if (playerData.value.happyLevel < (map.reduceHappy || 0)) {
        insufficientResources.value.push(`ความสุข ${map.reduceHappy}`);
      }

      if (activeTab.value === 'location' && selectedVehicle.value && selectedVehicle.value.fuelLevel < 1) {
        insufficientResources.value.push('น้ำมัน 1');
      }

      if (map.type === 'โลกวิญญาณ' && map.requiredSkills && map.requiredSkills.length > 0) {
        const missingSkills = map.requiredSkills.filter(skill => !playerData.value.learnedSkills.includes(skill));
        missingSkills.forEach(skill => insufficientResources.value.push(`ทักษะ ${skill}`));
      }

      if (map.type === 'โลกวิญญาณ' && map.requiredItems && map.requiredItems.length > 0) {
        const itemCounts = {};

        inventoryItems.value.forEach(item => {
          itemCounts[item.name] = (itemCounts[item.name] || 0) + 1;
        });

        map.requiredItems.forEach(itemName => {
          if ((itemCounts[itemName] || 0) < 1) {
            insufficientResources.value.push(`ไอเทม ${itemName} 1`);
          }
        });
      }
    };

    const exploreMap = async () => {
      if (!selectedMap.value) {
        error.value = 'กรุณาเลือกแผนที่ก่อน';
        return;
      }

      if (hasActiveEvent.value) {
        error.value = 'ตัวละครกำลังอยู่ในเหตุการณ์ ไม่สามารถออกสำรวจได้';
        return;
      }

      if (activeTab.value === 'location' && selectedVehicle.value && selectedVehicle.value.fuelLevel < 1) {
        error.value = 'น้ำมันไม่เพียงพอสำหรับการใช้งานพาหนะ';
        return;
      }

      isExploring.value = true;

      try {
        // บันทึกข้อมูลแผนที่ล่าสุดก่อนสำรวจ เพื่อให้ปุ่มลัดอัปเดตทันที
        const exploreContext = {
          mapId: selectedMap.value.id,
          mapName: selectedMap.value.name,
          mapType: activeTab.value,
          usedVehicle: activeTab.value === 'location' && !!selectedVehicle.value
        };
        saveLastExploreContext(exploreContext);

        if (selectedMap.value.type === 'สถานที่') {
          localStorage.setItem('lastVisitedLocationMapId', selectedMap.value.id);
          lastVisitedLocationMapId.value = selectedMap.value.id;
        } else if (selectedMap.value.type === 'โลกวิญญาณ') {
          localStorage.setItem('lastVisitedSupernaturalMapId', selectedMap.value.id);
          lastVisitedSupernaturalMapId.value = selectedMap.value.id;
        }

        await exploreMapByName(selectedMap.value.name, {
          mapType: activeTab.value,
          usedVehicle: activeTab.value === 'location' && !!selectedVehicle.value
        });

        selectedMap.value = null;
        selectedVehicle.value = null;
        insufficientResources.value = [];
        showMapDetails.value = false;
        unsubscribeBossStatuses();
        emit('cancelModal');
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการสำรวจแผนที่';
        console.error('Error exploring map:', err);
      } finally {
        isExploring.value = false;
      }
    };

    const cancelModal = () => {
      selectedMap.value = null;
      selectedVehicle.value = null;
      insufficientResources.value = [];
      mapPage.value = 0;
      showMapDetails.value = false;
      unsubscribeBossStatuses();
      emit('cancelModal');
    };

    const loadMoreMaps = () => {
      mapPage.value++;
    };

    const scrollToTop = () => {
      const scrollableDiv = document.querySelector('.max-h-96');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const handleScroll = () => {
      const scrollableDiv = document.querySelector('.max-h-96');
      if (scrollableDiv) {
        showBackToTop.value = scrollableDiv.scrollTop > 300;
      }
    };

    const addScrollListener = () => {
      const scrollableDiv = document.querySelector('.max-h-96');
      if (scrollableDiv) {
        scrollableDiv.addEventListener('scroll', handleScroll);
      }
    };

    const getResourceIcon = (item) => {
      if (item.includes('น้ำมัน')) return 'fas fa-gas-pump';
      if (item.includes('เลเวล')) return 'fas fa-level-up-alt';
      if (item.includes('ชื่อเสียง')) return 'fas fa-trophy';
      if (item.includes('เพชร')) return 'fas fa-gem';
      if (item.includes('เหรียญเงิน')) return 'fas fa-coins';
      if (item.includes('ทอง')) return 'fas fa-g';
      if (item.includes('แคช')) return 'fas fa-money-bill-wave';
      if (item.includes('พลังงาน')) return 'fas fa-bolt';
      if (item.includes('จำนวนห้อง')) return 'fas fa-door-closed';
      if (item.includes('ความสุข')) return 'fas fa-frown';
      if (item.includes('แผนที่')) return 'fas fa-map';
      if (item.includes('ทักษะ')) return 'fas fa-book';
      if (item.includes('ไอเทม')) return 'fas fa-box';

      return 'fas fa-question';
    };

    onMounted(async () => {
      loadHiddenMaps();
      loadShowLastVisitedFirst();
      loadActiveTab();
      await setupMapListener();
      initialFetchDone.value = true;
      setTimeout(addScrollListener, 0);

      storedVehicle.value = equippedVehicle.value;

      if (activeTab.value !== 'supernatural') {
        selectedVehicle.value = storedVehicle.value;
      }
    });

    onUnmounted(() => {
      unsubscribeBossStatuses();

      const scrollableDiv = document.querySelector('.max-h-96');
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    });

    return {
      isLoading,
      isLoadingAuth,
      error,
      user,
      maps,
      equippedVehicle,
      selectedMap,
      selectedVehicle,
      inventoryItems,
      itemDetails,
      displayedMaps,
      selectMap,
      exploreMap,
      cancelModal,
      insufficientResources,
      canAccessMap,
      getResourceIcon,
      activeTab,
      switchTab,
      isExploring,
      hasActiveEvent,
      hasMoreMaps,
      loadMoreMaps,
      showBackToTop,
      scrollToTop,
      hiddenMaps,
      toggleMapVisibility,
      isAdmin,
      showMapDetails,
      lastVisitedLocationMapId,
      lastVisitedSupernaturalMapId,
      showLastVisitedFirst,
      saveShowLastVisitedFirst,
      hasOccultismSkill,
      bossStatuses,
      getBossName
    };
  }
};
</script>
<style scoped>
.max-h-96 {
  max-height: 24rem;
}

.overflow-y-auto {
  overflow-y: auto;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

input[type="checkbox"]:focus + div {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.modal {
  animation: modalFade 0.2s ease-out;
}

.text-xxs {
  font-size: 0.7rem;
  line-height: 1rem;
}
</style>
